import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import RecurringTaskModal from '../RecurringTaskModal';


const TemplateEditor = ({ template, context, contextId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    assignees: [],
    priority: 'Normal',
    estimated_effort_value: '',
    estimated_effort_unit: 'hours',
    recurring_rule: {
      frequency: 'daily',
      interval: 1,
      daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri by default
      dueDateOffset: 0,
      dueDatePeriod: 'days'
    },
    recurring_due_time: '',
    recurring_start_time: '',
    generate_first_task: true
  });
  
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  
  useEffect(() => {
    fetchProjects();
    fetchTeamMembers();
    
    if (template) {
      // Edit mode - populate form
      setFormData({
        name: template.name.replace(' 🔄', ''), // Remove emoji for editing
        description: template.description || '',
        project_id: template.project_id,
        assignees: template.assignees || [],
        priority: template.priority,
        estimated_effort_value: template.estimated_effort_value || '',
        estimated_effort_unit: template.estimated_effort_unit || 'hours',
        recurring_rule: template.recurring_rule,
        recurring_due_time: template.recurring_due_time || '',
        recurring_start_time: template.recurring_start_time || '',
        generate_first_task: false // Don't generate when editing
      });
    }
  }, [template]);
  
  const fetchProjects = async () => {
    try {
      let url = `/projects`;
      
      // Filter by client if in client context
      if (context === 'client' && contextId) {
        url += `?client_id=${contextId}`;
      }
      
      const response = await api.get(url);
      
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };
  
  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/team');
      
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_id) {
      alert('Please select a project');
      return;
    }
    
    if (!formData.name.trim()) {
      alert('Please enter a template name');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (template) {
        // Update existing template
        await api.put(`/recurring-templates/${template.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new template
        await api.post(`/recurring-templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      onSave();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRecurringSave = (recurringData) => {
    setFormData(prev => ({
      ...prev,
      recurring_rule: recurringData.recurring_rule,
      recurring_due_time: recurringData.recurring_due_time,
      recurring_start_time: recurringData.recurring_start_time
    }));
    setShowRecurringModal(false);
  };
  
  const getFrequencyDisplay = () => {
    const rule = formData.recurring_rule;
    if (!rule) return 'Not configured';
    
    if (rule.frequency === 'daily') {
      if (rule.daysOfWeek && rule.daysOfWeek.length > 0 && rule.daysOfWeek.length < 7) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `Daily: ${rule.daysOfWeek.map(d => days[d]).join(', ')}`;
      }
      return rule.interval > 1 ? `Every ${rule.interval} days` : 'Daily';
    }
    
    return rule.frequency.charAt(0).toUpperCase() + rule.frequency.slice(1);
  };
  
  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {template ? 'Edit Recurring Template' : 'Create Recurring Template'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Daily Report"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                🔄 emoji will be added automatically
              </p>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Optional description"
              />
            </div>
            
            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project *
              </label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData(prev => ({ ...prev, project_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Assignees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assignees
              </label>
              <select
                multiple
                value={formData.assignees}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setFormData(prev => ({ ...prev, assignees: selected }));
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                size={4}
              >
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name} ({member.email})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Hold Ctrl/Cmd to select multiple
              </p>
            </div>
            
            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            
            {/* Estimated Effort */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Effort
                </label>
                <input
                  type="number"
                  value={formData.estimated_effort_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_effort_value: e.target.value ? parseInt(e.target.value) : '' }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 2"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <select
                  value={formData.estimated_effort_unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_effort_unit: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
            
            {/* Recurring Schedule */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recurring Schedule *
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900 mb-1">
                      {getFrequencyDisplay()}
                    </div>
                    {formData.recurring_due_time && (
                      <div className="text-sm text-gray-600">
                        Due at: {formData.recurring_due_time}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowRecurringModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Configure
                  </button>
                </div>
              </div>
            </div>
            
            {/* Generate First Task (only for new templates) */}
            {!template && (
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="generate_first_task"
                  checked={formData.generate_first_task}
                  onChange={(e) => setFormData(prev => ({ ...prev, generate_first_task: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="generate_first_task" className="text-sm text-gray-700">
                  Generate first task immediately
                </label>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Recurring Task Modal */}
      {showRecurringModal && (
        <RecurringTaskModal
          isOpen={showRecurringModal}
          onClose={() => setShowRecurringModal(false)}
          onSave={handleRecurringSave}
          initialData={{
            isRecurringTemplate: true,
            recurring_rule: formData.recurring_rule,
            recurring_due_time: formData.recurring_due_time,
            recurring_start_time: formData.recurring_start_time
          }}
        />
      )}
    </>
  );
};

export default TemplateEditor;
